import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  Truck, 
  Package, 
  Clock, 
  Award, 
  ChevronRight,
  ExternalLink,
  ArrowLeft,
  PackageCheck,
  Bike,
  Tv,
  Hammer,
  ShoppingBag,
  Home,
  Globe,
  MessageCircle,
  Shield
} from "lucide-react";

// Assets
const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_c0ab0141-6109-47e0-85ac-e7ed5a0f845a/artifacts/3ntwifq3_20be29a4-2aba-4131-8551-a74a93b6c074.jpeg",
  logoFull: "https://customer-assets.emergentagent.com/job_logistics-hub-426/artifacts/xmbazgh9_tomalogic.jpeg",
  hero: "https://customer-assets.emergentagent.com/job_logistics-hub-426/artifacts/8ddkxkd5_IMG_2157.jpeg",
  services: {
    roofbox: "https://customer-assets.emergentagent.com/job_logistics-hub-426/artifacts/r24qz7h6_IMG_2162.jpeg",
    trailers: "https://customer-assets.emergentagent.com/job_logistics-hub-426/artifacts/8zyqjlhe_IMG_2170.jpeg",
    transport: "https://customer-assets.emergentagent.com/job_logistics-hub-426/artifacts/viqit3gt_IMG_2178.jpeg",
    parts: "https://customer-assets.emergentagent.com/job_logistics-hub-426/artifacts/el9k4ffv_IMG_2149.jpeg",
  },
  about: "https://customer-assets.emergentagent.com/job_logistics-hub-426/artifacts/hla3z71u_IMG_2164.jpeg",
};

const CONTACT = {
  phone: "+48 666 905 905",
  email: "tomalogic1@gmail.com",
  allegro: "https://business.allegro.pl/uzytkownik/Tomalogic",
  olx: "https://www.olx.pl/oferty/user/AhB9K/",
};

// Navigation Component
const Navbar = ({ isScrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  const navLinks = [
    { name: "Oferta", href: "#uslugi" },
    { name: "Allegro", href: CONTACT.allegro, external: true },
    { name: "OLX", href: CONTACT.olx, external: true },
    { name: "O nas", href: "#o-nas" },
    { name: "Kontakt", href: "#kontakt" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-black/90 backdrop-blur-md border-b border-white/5 py-3 md:py-4"
          : "bg-transparent py-4 md:py-6"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center flex-1 md:flex-none mr-4 md:mr-0" data-testid="logo-link">
            {/* Mobile: Square logo + text */}
            <div className="md:hidden flex items-center gap-2">
              <img
                src={ASSETS.logo}
                alt="Tomalogic"
                className="h-10 w-auto"
              />
              <span className="font-heading text-xl font-bold text-gray-300 uppercase tracking-wide">
                Tomalogic
              </span>
            </div>
            {/* Desktop: Square logo */}
            <img
              src={ASSETS.logo}
              alt="Tomalogic"
              className="hidden md:block h-12 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                target={link.external ? "_blank" : undefined}
                rel={link.external ? "noopener noreferrer" : undefined}
                className="nav-link text-sm font-medium tracking-widest uppercase text-gray-300 hover:text-white transition-colors"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
                {link.external && <ExternalLink className="inline-block ml-1 w-3 h-3" />}
              </a>
            ))}
          </div>

          {/* Phone - Desktop */}
          <a
            href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
            className="hidden md:flex items-center gap-2 text-toma-gold hover:text-toma-gold-hover transition-colors font-semibold"
            data-testid="nav-phone"
          >
            <Phone className="w-5 h-5" />
            <span>{CONTACT.phone}</span>
          </a>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-white"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 space-y-6">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  target={link.external ? "_blank" : undefined}
                  rel={link.external ? "noopener noreferrer" : undefined}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-lg font-medium tracking-wider uppercase text-gray-300 hover:text-toma-gold transition-colors"
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                  {link.external && <ExternalLink className="inline-block ml-2 w-4 h-4" />}
                </a>
              ))}
              <a
                href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                className="flex items-center gap-3 text-toma-gold text-lg font-semibold pt-4 border-t border-white/10"
                data-testid="mobile-phone"
              >
                <Phone className="w-5 h-5" />
                {CONTACT.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden w-full"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={ASSETS.hero}
          alt="Tomalogic - profesjonalny transport"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-0">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-toma-gold text-sm md:text-base font-medium tracking-[0.3em] uppercase mb-6"
          >
            Sprzedaż • Wynajem • Transport
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter uppercase text-white leading-[0.9] mb-8"
          >
            Tomalogic
            <span className="block text-3xl md:text-4xl lg:text-5xl text-gray-400 mt-4">
              Rozwiązania dla kierowców
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-lg md:text-xl text-gray-400 leading-relaxed mb-12 max-w-xl"
          >
            Profesjonalne usługi transportowe i wynajem bagażników dachowych.
            Dla firm i pasjonatów motoryzacji.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#uslugi"
              className="btn-gold bg-toma-gold text-black hover:bg-toma-gold-hover font-bold uppercase tracking-wider px-8 py-4 text-center transition-all duration-300"
              data-testid="hero-cta-primary"
            >
              Zobacz ofertę
            </a>
            <a
              href="#kontakt"
              className="bg-transparent border border-white/20 text-white hover:bg-white hover:text-black font-semibold uppercase tracking-wider px-8 py-4 text-center transition-all duration-300"
              data-testid="hero-cta-secondary"
            >
              Kontakt
            </a>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2 text-gray-500">
          <span className="text-xs tracking-widest uppercase">Przewiń</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ChevronRight className="w-5 h-5 rotate-90" />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      id: "bagazniki",
      title: "Bagażniki dachowe",
      description: "Sprzedaż i wynajem bagażników dachowych oraz bazowych. Dobieramy rozwiązania do większości modeli samochodów.",
      image: ASSETS.services.roofbox,
      cta: "Sprawdź",
      span: "md:col-span-1 lg:col-span-6",
    },
    {
      id: "transport",
      title: "Usługi transportowe",
      description: "Transport samochodów, motocykli, quadów i towarów. Bezpiecznie i terminowo.",
      image: ASSETS.services.transport,
      cta: "Zamów",
      span: "md:col-span-1 lg:col-span-6",
      highlight: true,
      link: "/transport",
      internal: true,
    },
    {
      id: "czesci",
      title: "Części samochodowe",
      description: "Oryginalne używane części samochodowe. Każdy produkt zgodny ze zdjęciem.",
      image: ASSETS.services.parts,
      cta: "Przeglądaj",
      span: "md:col-span-2 lg:col-span-12",
      link: CONTACT.allegro,
    },
  ];

  return (
    <section id="uslugi" className="py-24 md:py-32 bg-toma-dark" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-toma-gold text-sm font-medium tracking-[0.3em] uppercase mb-4">
            Nasze usługi
          </p>
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight uppercase text-white">
            Usługi transportowe
          </h2>
          <p className="text-gray-400 text-lg mt-4 max-w-xl">
            Wybierz usługę dopasowaną do Twoich potrzeb.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`service-card group relative overflow-hidden bg-toma-card border border-toma-border ${service.span} min-h-[350px] md:min-h-[400px]`}
              data-testid={`service-card-${service.id}`}
            >
              {/* Image */}
              <div className="absolute inset-0">
                <img
                  src={service.image}
                  alt={service.title}
                  className="service-image w-full h-full object-cover transition-transform duration-700"
                />
                <div className="service-overlay absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent transition-all duration-500" />
              </div>

              {/* Content */}
              <div className="relative z-10 h-full flex flex-col justify-end p-8">
                {service.highlight && (
                  <span className="inline-block bg-toma-gold text-black text-xs font-bold tracking-wider uppercase px-3 py-1 mb-4 w-fit">
                    Popularne
                  </span>
                )}
                <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-wide uppercase text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-6">
                  {service.description}
                </p>
                <a
                  href={service.link || "#kontakt"}
                  target={service.link && !service.internal ? "_blank" : undefined}
                  rel={service.link && !service.internal ? "noopener noreferrer" : undefined}
                  className="inline-flex items-center gap-2 text-toma-gold font-semibold uppercase tracking-wider text-sm hover:gap-4 transition-all duration-300"
                  data-testid={`service-cta-${service.id}`}
                  onClick={(e) => {
                    if (service.internal) {
                      e.preventDefault();
                      window.location.href = service.link;
                    }
                  }}
                >
                  {service.cta}
                  <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Us Section
const WhyUsSection = () => {
  const features = [
    {
      icon: Truck,
      title: "Nowoczesna flota",
      description: "Dysponujemy sprzętem najwyższej klasy, gwarantującym bezpieczeństwo transportu i niezawodność wynajmu.",
    },
    {
      icon: Clock,
      title: "Terminowość",
      description: "Szanujemy Twój czas. Zawsze docieramy na miejsce zgodnie z ustalonym harmonogramem.",
    },
    {
      icon: Award,
      title: "Doświadczenie",
      description: "Lata praktyki w branży motoryzacyjnej pozwalają nam świadczyć usługi na poziomie premium.",
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-toma-card" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-toma-gold text-sm font-medium tracking-[0.3em] uppercase mb-4">
            Zaufanie klientów
          </p>
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight uppercase text-white">
            Dlaczego Tomalogic?
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="feature-item p-8 bg-toma-surface border border-toma-border hover:border-toma-gold/30 transition-all duration-400"
              data-testid={`feature-${index}`}
            >
              <feature.icon className="feature-icon w-12 h-12 text-gray-500 mb-6 transition-all duration-300" />
              <h3 className="font-heading text-xl md:text-2xl font-semibold tracking-wide uppercase text-white mb-4">
                {feature.title}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="o-nas" className="py-24 md:py-32 bg-toma-dark" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={ASSETS.about}
                alt="O firmie Tomalogic"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-toma-gold text-sm font-medium tracking-[0.3em] uppercase mb-4">
              O nas
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-8">
              Pasja do motoryzacji
            </h2>
            <div className="space-y-6 text-gray-400 text-lg leading-relaxed">
              <p>
                <strong className="text-white">Tomalogic</strong> to młoda, dynamicznie rozwijająca się firma 
                z ugruntowaną pozycją na rynku.
              </p>
              <p>
                Specjalizujemy się w sprzedaży, wynajmie oraz transporcie. 
                Łączymy doświadczenie motoryzacyjne z praktycznym podejściem do potrzeb klienta.
              </p>
              <p>
                Oferujemy profesjonalny wynajem bagażników dachowych oraz kompleksowe usługi transportowe. 
                Naszym priorytetem jest jakość obsługi i zadowolenie klienta.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-24 md:py-32 bg-toma-gold relative overflow-hidden" data-testid="cta-section">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 35px,
            rgba(0,0,0,0.1) 35px,
            rgba(0,0,0,0.1) 70px
          )`
        }} />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase text-black mb-6">
            Potrzebujesz bagażnika lub transportu?
          </h2>
          <p className="text-black/70 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
            Skontaktuj się z nami — doradzimy i dobierzemy najlepsze rozwiązanie.
          </p>
          <a
            href="#kontakt"
            className="inline-block bg-black text-white hover:bg-white hover:text-black font-bold uppercase tracking-wider px-10 py-5 transition-all duration-300"
            data-testid="cta-button"
          >
            Skontaktuj się z nami
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="kontakt" className="py-24 md:py-32 bg-toma-dark" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-toma-gold text-sm font-medium tracking-[0.3em] uppercase mb-4">
              Kontakt
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-8">
              Skontaktuj się z nami
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-12">
              Zadzwoń lub napisz. Jesteśmy do Twojej dyspozycji. 
              Odpowiemy na wszystkie pytania dotyczące naszych usług.
            </p>

            <div className="space-y-8">
              {/* Phone */}
              <a
                href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                className="contact-link flex items-center gap-6 group"
                data-testid="contact-phone"
              >
                <div className="w-16 h-16 bg-toma-card border border-toma-border flex items-center justify-center group-hover:border-toma-gold/50 transition-colors">
                  <Phone className="w-6 h-6 text-toma-gold" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">Telefon</p>
                  <p className="text-2xl md:text-3xl font-heading font-bold text-white">
                    {CONTACT.phone}
                  </p>
                </div>
              </a>

              {/* Email */}
              <a
                href={`mailto:${CONTACT.email}`}
                className="contact-link flex items-center gap-6 group"
                data-testid="contact-email"
              >
                <div className="w-16 h-16 bg-toma-card border border-toma-border flex items-center justify-center group-hover:border-toma-gold/50 transition-colors">
                  <Mail className="w-6 h-6 text-toma-gold" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">E-mail</p>
                  <p className="text-xl md:text-2xl font-heading font-bold text-white">
                    {CONTACT.email}
                  </p>
                </div>
              </a>

              {/* Location */}
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 bg-toma-card border border-toma-border flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-toma-gold" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">Lokalizacja</p>
                  <p className="text-lg md:text-xl font-heading font-bold text-white">
                    Niedźwiedź 368
                  </p>
                  <p className="text-gray-400 text-sm">
                    34-735 Niedźwiedź
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Shop CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center"
          >
            <div className="w-full p-10 bg-toma-card border border-toma-border">
              <Package className="w-12 h-12 text-toma-gold mb-6" />
              <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-wide uppercase text-white mb-4">
                Odwiedź nasze sklepy
              </h3>
              <p className="text-gray-400 leading-relaxed mb-8">
                Znajdź bagażniki dachowe, belki bazowe, części samochodowe i więcej. 
                Sprawdź nasze oferty.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={CONTACT.allegro}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 btn-gold bg-toma-gold text-black hover:bg-toma-gold-hover font-bold uppercase tracking-wider px-6 py-4 transition-all duration-300"
                  data-testid="contact-allegro-link"
                >
                  Allegro
                  <ExternalLink className="w-5 h-5" />
                </a>
                <a
                  href={CONTACT.olx}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-transparent border border-white/20 text-white hover:bg-white hover:text-black font-bold uppercase tracking-wider px-6 py-4 transition-all duration-300"
                  data-testid="contact-olx-link"
                >
                  OLX
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Transport Page
const TransportPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const transportItems = [
    { icon: PackageCheck, text: "paczki i przesyłki" },
    { icon: Bike, text: "motocykle, rowery, quady" },
    { icon: Tv, text: "sprzęt AGD / RTV" },
    { icon: Hammer, text: "materiały budowlane" },
    { icon: ShoppingBag, text: "zakupy ze sklepów" },
    { icon: Home, text: "przeprowadzki prywatne i firmowe" },
  ];

  const features = [
    { icon: Globe, text: "Transport krajowy i międzynarodowy" },
    { icon: MessageCircle, text: "Stały kontakt podczas transportu" },
    { icon: Shield, text: "Ubezpieczenie OCP" },
  ];

  return (
    <div className="bg-toma-dark min-h-screen overflow-x-hidden" data-testid="transport-page">
      <Navbar
        isScrolled={isScrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      
      <main className="pt-24">
        {/* Hero */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={ASSETS.services.transport}
              alt="Transport"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-toma-dark via-toma-dark/90 to-toma-dark" />
          </div>

          <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12">
            {/* Back link */}
            <motion.a
              href="/"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 text-gray-400 hover:text-toma-gold transition-colors mb-12"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Powrót do strony głównej
            </motion.a>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-toma-gold text-sm font-medium tracking-[0.3em] uppercase mb-4">
                Tomalogic
              </p>
              <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase text-white mb-8">
                Usługi transportowe
              </h1>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-center gap-4 mb-8"
            >
              <div className="w-14 h-14 bg-toma-gold/20 border border-toma-gold/30 flex items-center justify-center">
                <Truck className="w-7 h-7 text-toma-gold" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white">
                Transport Polska – Szwecja – Norwegia
              </h2>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-4"
            >
              Szybki i bezpieczny transport rzeczy, pojazdów i towarów.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg text-gray-400 mb-12"
            >
              Odbiór i dostawa pod wskazany adres.
            </motion.p>
          </div>
        </section>

        {/* What we transport */}
        <section className="py-16 md:py-24 bg-toma-card">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-2xl md:text-3xl font-bold tracking-wide uppercase text-white mb-12"
            >
              Transportujemy m.in.:
            </motion.h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {transportItems.map((item, index) => (
                <motion.div
                  key={item.text}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-toma-surface border border-toma-border hover:border-toma-gold/30 transition-colors"
                  data-testid={`transport-item-${index}`}
                >
                  <div className="w-10 h-10 bg-toma-gold/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-toma-gold" />
                  </div>
                  <span className="text-lg text-gray-300">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 md:py-24 bg-toma-dark">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.text}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.15 }}
                  className="flex items-center gap-4 p-6 bg-toma-gold/10 border border-toma-gold/30"
                  data-testid={`feature-${index}`}
                >
                  <feature.icon className="w-6 h-6 text-toma-gold flex-shrink-0" />
                  <span className="text-white font-medium">{feature.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-32 bg-toma-gold">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Phone className="w-16 h-16 text-black mx-auto mb-8" />
              <p className="text-black/70 text-lg mb-4">Zadzwoń i zamów transport</p>
              <a
                href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                className="font-heading text-4xl md:text-6xl font-bold text-black hover:text-black/70 transition-colors"
                data-testid="transport-phone"
              >
                {CONTACT.phone}
              </a>
              <p className="mt-12 font-heading text-2xl font-bold text-black uppercase tracking-wider">
                Tomalogic
              </p>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 bg-black border-t border-toma-border" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          {/* Logo & Tagline */}
          <div className="text-center md:text-left">
            <img
              src={ASSETS.logo}
              alt="Tomalogic"
              className="h-10 w-auto mb-3 mx-auto md:mx-0"
            />
            <p className="text-gray-500 text-sm tracking-wider">
              Sprzedaż | Wynajem | Transport
            </p>
          </div>

          {/* Contact */}
          <div className="flex flex-col md:flex-row items-center gap-6 text-sm">
            <a
              href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
              className="flex items-center gap-2 text-gray-400 hover:text-toma-gold transition-colors"
              data-testid="footer-phone"
            >
              <Phone className="w-4 h-4" />
              {CONTACT.phone}
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              className="flex items-center gap-2 text-gray-400 hover:text-toma-gold transition-colors"
              data-testid="footer-email"
            >
              <Mail className="w-4 h-4" />
              {CONTACT.email}
            </a>
          </div>

          {/* Copyright */}
          <p className="text-gray-600 text-sm">
            © {new Date().getFullYear()} Tomalogic
          </p>
        </div>
      </div>
    </footer>
  );
};

// Homepage Component
const HomePage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="bg-toma-dark min-h-screen overflow-x-hidden" data-testid="app">
      <Navbar
        isScrolled={isScrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <main>
        <HeroSection />
        <ServicesSection />
        <WhyUsSection />
        <AboutSection />
        <CTASection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

// Main App Component with Router
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/transport" element={<TransportPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
